<template>
    <!-- Conteneur principal du composant historique des recherches -->
    <div class="search-history-container">

        <!-- Lien déclencheur pour ouvrir la modale d'historique -->
        <!-- @click.prevent empêche le comportement par défaut du lien -->
        <a id="search-history-btn" href="#" @click.prevent="showModal" class="search-history-link">
            Accéder à l'historique des recherches
        </a>

        <!-- Composant Modal Bootstrap personnalisé -->
        <modal-dialog title="Historique des recherches" :show="showHistoryModal" @close="handleCloseModal"
            confirm-text="Valider" cancel-text="Annuler" dialog-id="search-history-modal">

            <!-- Contenu principal de la modale -->
            <div class="modal-form">

                <!-- Conteneur du tableau avec scroll horizontal si nécessaire -->
                <div class="table-container">

                    <!-- Tableau principal affichant l'historique des recherches -->
                    <table class="table table-bordered">
                        <caption class="sr-only">Tableau de l'historique des recherches effectuées</caption>
                        <!-- En-tête du tableau -->
                        <thead>
                            <tr>
                                <!-- Colonne numéro de ligne -->
                                <th class="col-xs-1 text-center">#</th>
                                <!-- Colonne date de la recherche -->
                                <th class="col-xs-2 text-center">Date</th>
                                <!-- Colonne détails de la recherche -->
                                <th>Recherche</th>
                                <!-- Colonne nombre de résultats -->
                                <th class="col-xs-2 text-center">Résultats</th>
                                <!-- Colonne actions (bouton rejouer) -->
                                <th class="col-xs-1 text-center">Action</th>
                            </tr>
                        </thead>

                        <!-- Corps du tableau avec boucle sur les données d'historique -->
                        <tbody>
                            <!-- v-for itère sur chaque élément de l'historique -->
                            <!-- :key utilise l'index pour identifier chaque ligne -->
                            <tr v-for="(item, index) in historyData" :key="index">

                                <!-- Numéro de ligne (index + 1 pour commencer à 1) -->
                                <td class="col-xs-1 text-center">{{ index + 1 }}</td>

                                <!-- Date formatée avec tooltip au survol -->
                                <td class="col-xs-2 text-nowrap text-center"
                                    :title="formatDate(item.date, 'DD/MM/YYYY HH:mm:ss')">
                                    {{ formatDate(item.date, 'DD/MM/YYYY HH:mm') }}
                                </td>

                                <!-- Détails de la recherche (requête, type, etc.) -->
                                <td class="search-column">{{ displaySearch(item) }}</td>

                                <!-- Nombre de résultats trouvés -->
                                <td class="col-xs-2 resultats-column">{{ formatResults(item) }}</td>

                                <!-- Bouton d'action pour rejouer la recherche -->
                                <td class="col-xs-1 text-center">
                                    <!-- Bouton pour rejouer la recherche -->
                                    <button class="btn btn--primary btn--plain btn--icon-only"
                                        :title="`Rejouer cette recherche`" @click="replaySearch(item)">

                                        <!-- Icône SVG sprite reset -->
                                        <svg class="svg-icon svg-reset" aria-hidden="true" focusable="false">
                                            <use
                                                xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#reset">
                                            </use>
                                        </svg>

                                        <!-- Texte caché pour les lecteurs d'écran -->
                                        <span class="sr-only">Lancer la recherche</span>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Section bouton de suppression globale -->
            <div class="text-center">
                <button class="btn btn--primary btn--plain btn--icon-before" @click="clearAllHistory">

                    <!-- Icône de corbeille -->
                    <svg class="svg-icon svg-trash" aria-hidden="true" focusable="false">
                        <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#trash"></use>
                    </svg>

                    <!-- Texte du bouton -->
                    Tout supprimer
                </button>
            </div>

        </modal-dialog>
    </div>
</template>

<script>
import ModalDialog from './ModalDialog.vue';
import { HistoryUtils } from '../../../common/historyUtils.js';

function buildLanguageDescription(item, formatLanguageCode) {
    const itemLanguages = Array.isArray(item.languages) ? item.languages : (item.languages ? [item.languages] : []);

    if (itemLanguages.length === 1) {
        return ` - Langue : ${formatLanguageCode(itemLanguages[0])}`;
    }

    if (itemLanguages.length > 1) {
        const languageLabels = itemLanguages.map(lang => formatLanguageCode(lang)).join(', ');
        return ` - Langues : ${languageLabels}`;
    }

    return ` - Langues : Toutes les langues `;
}

export default {
    name: 'SearchHistory',

    components: {
        ModalDialog,
    },

    data() {
        return {
            showHistoryModal: false,
            historyData: [], // Données réactives de l'historique
        };
    },

    methods: {
        /**
         * Charge les données de l'historique depuis localStorage
         */
        loadHistoryData() {
            this.historyData = HistoryUtils.getHistory();
        },

        /**
         * Gère les changements de localStorage (synchronisation entre onglets)
         * @param {StorageEvent} event - Événement de changement de storage
         */
        handleStorageChange(event) {
            if (event.key === 'search_history') {
                this.loadHistoryData();
            }
        },

        /**
         * Méthode publique pour forcer la mise à jour des données
         * Peut être appelée par le composant parent après avoir ajouté une recherche
         */
        refreshHistory() {
            this.loadHistoryData();
        },

        /**
         * Affiche le modal de l'historique des recherches
         */
        showModal() {
            this.loadHistoryData(); // Recharge les données à chaque ouverture
            this.showHistoryModal = true;
        },

        /**
         * Ferme le modal de l'historique des recherches
         */
        handleCloseModal() {
            this.showHistoryModal = false;
        },

        /**
         * Formate une date selon le format spécifié
         * @param {Date} date - La date à formater
         * @param {string} format - Le format de sortie
         * @returns {string} La date formatée
         */
        formatDate(date, format) {
            if (!date) return '';

            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');

            if (format === 'DD/MM/YYYY HH:mm:ss') {
                return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
            } else if (format === 'DD/MM/YYYY HH:mm') {
                return `${day}/${month}/${year} ${hours}:${minutes}`;
            }

            return d.toLocaleDateString('fr-FR');
        },

        /**
         * Affiche le type de recherche et tous les paramètres
         * @param {Object} item - L'élément de l'historique
         * @returns {string} La description complète de la recherche
         */
        displaySearch(item) {
            let description = '';

            // Type de recherche
            const searchType = item.advanced ? 'Recherche en mode étendu' : 'Recherche';
            description += `${searchType} : "${item.query}"`;

            // Affichage des langues (tolère les anciennes entrées où languages était une simple chaîne)
            description += buildLanguageDescription(item, this.formatLanguageCode);

            // Affichage des types
            if (item.types && item.types.length > 0) {
                if (item.types.includes('all') || item.types.length > 1) {
                    description += ` - Types : Tous`;
                } else {
                    description += ` - Type : ${this.formatTypeCode(item.types[0])}`;
                }
            }

            return description;
        },

        /**
         * Formate le code de langue en libellé lisible
         * @param {string} langCode - Code de la langue (ex: 'fr', 'en')
         * @returns {string} Libellé de la langue
         */
        formatLanguageCode(langCode) {
            const languageMap = {
                'fr': 'Français',
                'en': 'Anglais',
                'es': 'Espagnol',
                'de': 'Allemand',
                'it': 'Italien',
                'all': 'Toutes les langues'
            };
            return languageMap[langCode] || langCode.toUpperCase();
        },

        /**
         * Formate le code de type en libellé lisible
         * @param {string} typeCode - Code du type (ex: 'terminologie', 'nomenclature')
         * @returns {string} Libellé du type
         */
        formatTypeCode(typeCode) {
            const typeMap = {
                'all': 'Tous les types',
                'terminologie': 'Terminologie',
                'nomenclature': 'Nomenclature',
                'jeux-de-valeurs': 'Jeux de valeurs',
                'cas-usage': 'Cas d\'usage'
            };
            return typeMap[typeCode] || typeCode.charAt(0).toUpperCase() + typeCode.slice(1);
        },

        /**
         * Formate l'affichage des résultats (Concepts et Terminologies)
         * @param {Object} item - L'élément de l'historique
         * @returns {string} Résultats formatés
         */
        formatResults(item) {
            const searchTypes = item.types || [];
            const hasConceptsType = searchTypes.includes('concept');
            const hasTerminoType = searchTypes.includes('termino');

            let results = [];

            // Afficher les concepts seulement si le type "concepts" est présent
            if (hasConceptsType) {
                const conceptsCount = item.conceptsCount !== undefined ? item.conceptsCount : 0;
                results.push(`Concepts (${conceptsCount})`);
            }

            // Afficher les terminologies seulement si le type "termino" est présent
            if (hasTerminoType) {
                const terminologiesCount = item.terminologiesCount !== undefined ? item.terminologiesCount : 0;
                results.push(`Terminologies (${terminologiesCount})`);
            }

            // Si aucun type spécifique n'est trouvé, afficher un message par défaut
            if (results.length === 0) {
                return 'Aucun résultat';
            }

            return results.join(' - ');
        },

        /**
         * Rejoue une recherche à partir de l'historique
         * @param {Object} item - L'élément de l'historique à rejouer
         */
        replaySearch(item) {
            // Émet un événement vers le composant parent pour rejouer la recherche
            this.$emit('replay-search', {
                query: item.query,
                language: Array.isArray(item.languages) ? item.languages[0] : item.languages,
                types: item.types,
                options: {
                    extendedSearch: item.advanced
                }
            });

            // Ferme le modal après avoir lancé la recherche
            this.handleCloseModal();
        },

        /**
         * Supprime tout l'historique des recherches
         */
        clearAllHistory() {
            HistoryUtils.clearHistory();
            this.loadHistoryData(); // Recharge les données après suppression
            this.$emit('history-cleared');
            this.handleCloseModal();
        }
    },

    mounted() {
        // Charge les données initiales
        this.loadHistoryData();

        // Écoute les changements de localStorage (pour les onglets multiples)
        window.addEventListener('storage', this.handleStorageChange);
    },

    beforeDestroy() {
        // Nettoie l'écouteur d'événement
        window.removeEventListener('storage', this.handleStorageChange);
    }
};
</script>

<style scoped>
.search-history-container {
    display: inline-block;
}

/* Styles pour agrandir la popup modale */
::v-deep .modal-dialog {
    max-width: 90vw;
    width: 90vw;
    margin: 1.75rem auto;
}

::v-deep .modal-content {
    width: 100%;
}

/* Styles pour le modal et le tableau */
.modal-form {
    max-height: 60vh;
    overflow: auto;
    padding-right: 10px;
    /* Compensation pour la scrollbar */
}

.table th {
    background-color: #f8f9fa;
    font-weight: 600;
    border-bottom: 2px solid #dee2e6;
    position: sticky;
    top: 0;
    z-index: 10;
}

.table td {
    vertical-align: middle;
    white-space: nowrap;
}

.table td.search-column {
    white-space: normal !important;
    word-wrap: break-word;
    word-break: break-word;
    max-width: 300px;
    min-width: 200px;
}

.text-nowrap {
    white-space: nowrap;
}

.text-right {
    text-align: right;
}

.text-center {
    text-align: center;
    margin-top: 20px;
}

/* Styles pour les boutons d'action */
.btn--icon-only {
    padding: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.btn--icon-before {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.svg-icon {
    width: 16px;
    height: 16px;
}

/* Style responsive pour petits écrans */
@media (max-width: 768px) {
    ::v-deep .modal-dialog {
        max-width: 95vw;
        width: 95vw;
        margin: 1rem auto;
    }

    .modal-form {
        max-height: 70vh;
        /* Hauteur adaptée pour mobile */
        font-size: 14px;
    }

    .table th,
    .table td {
        padding: 8px 4px;
        white-space: nowrap;
    }

    .table td.search-column {
        white-space: normal !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        max-width: 150px;
        min-width: 120px;
        /* Largeur adaptée pour mobile */
    }

    .table td.resultats-column {
        white-space: normal !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        /* Largeur adaptée pour mobile */
    }
}
</style>